/**
 * Crawlable pagination for every listing route — the browse containers AND the
 * Search App. (item 11 for browse; unified onto both by item 15)
 *
 * Core's ListingBody paginates with Semantic UI's `<Pagination>`, whose items
 * are `<a class="item" tabindex="0">` with an onClick and **no href**. That is
 * fine for a person with JavaScript and nothing to anyone else: with 25 results
 * a page, the first page of `/documents` exposes 25 of the 1,570 standards /
 * 7,366 directives and there is no link path to the rest. Every document is in
 * the sitemap, but a sitemap conveys no context, no anchor text and no relative
 * importance — the whole point of an internal link graph.
 *
 * This renders the same control as real `<a href="?page=N">` links. It replaces
 * core's rather than sitting beside it (theme/page.less hides
 * `.pagination-wrapper` inside `.pp-page`) — one pagination, and the one that
 * works without JavaScript.
 *
 * On /search the crawl argument does not apply (that route is `noindex,follow`
 * by design — see the Search App's SEO note), but three others do, and they are
 * why item 15 unified rather than leaving the two pagers alone: page 2 of a
 * search becomes a real, shareable URL; its links become middle-clickable and
 * open-in-new-tab-able like every other link on the site; and the control looks
 * and behaves identically on both routes, which is the whole point of the item.
 *
 * `rel="prev"`/`rel="next"` are advisory only; Google stopped using them as an
 * indexing signal in 2019. They stay because they are correct and assistive
 * technology still uses them.
 */
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { pageHref, pageWindow } from '../Browse/browseState';

const ResultsPager = ({ id, pathname, search, pageKey, currentPage, bSize }) => {
  const total = useSelector(
    (state) => state.querystringsearch?.subrequests?.[id]?.total,
  );
  const size = bSize || 25;
  const totalPages = total ? Math.ceil(total / size) : 0;

  // item 29: a ?page= change never fires core ScrollToTop (the pathname is
  // unchanged), so the viewport used to stay parked wherever the old page
  // left it -- usually on this pager, under a screenful of unrelated new
  // rows. On every page change after mount, bring the results header back
  // into view and move focus onto the role="status" results line, which
  // also re-announces the new count to screen readers.
  const prevPage = React.useRef(currentPage);
  React.useEffect(() => {
    if (prevPage.current === currentPage) return;
    prevPage.current = currentPage;
    const anchor =
      document.querySelector('.search-results-count-sort') ||
      document.querySelector('.pp-page');
    if (!anchor) return;
    anchor.scrollIntoView();
    const target = anchor.querySelector('[role="status"]') || anchor;
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }, [currentPage]);

  if (totalPages < 2) return null;

  const page = Math.min(Math.max(currentPage, 1), totalPages);
  const href = (p) => pageHref(pathname, search, pageKey, p);
  const numbers = pageWindow(page, totalPages);

  return (
    <nav className="pp-results-pager" aria-label="Result pages">
      {page > 1 && (
        <Link className="pp-results-pager__step" rel="prev" to={href(page - 1)}>
          Previous
        </Link>
      )}
      <ol className="pp-results-pager__pages">
        {numbers.map((p, i) => (
          <React.Fragment key={p}>
            {i > 0 && numbers[i - 1] !== p - 1 && (
              <li className="pp-results-pager__gap" aria-hidden="true">
                …
              </li>
            )}
            <li>
              {p === page ? (
                <span
                  className="pp-results-pager__page is-active"
                  aria-current="page"
                >
                  {p}
                </span>
              ) : (
                <Link className="pp-results-pager__page" to={href(p)}>
                  {p}
                </Link>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
      {page < totalPages && (
        <Link className="pp-results-pager__step" rel="next" to={href(page + 1)}>
          Next
        </Link>
      )}
    </nav>
  );
};

export default ResultsPager;
